/**
 * Sound effects using Web Audio API
 * No external files needed - generates sounds programmatically
 */

// Create audio context
let audioContext: AudioContext | null = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  
  // Resume context if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume().then(() => {
      console.log('Audio context resumed');
    }).catch((err) => {
      console.warn('Could not resume audio context:', err);
    });
  }
  
  return audioContext;
};

// Initialize audio on first user interaction (required for Android/iOS)
export const initializeAudioOnUserInteraction = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
};

// Check if sound effects are enabled
const isSoundEnabled = (): boolean => {
  const savedPref = localStorage.getItem('soundEffectsEnabled');
  return savedPref === null ? true : savedPref === 'true'; // Default to true
};

// Play correct answer sound (pleasant upward chime)
export const playCorrectSound = () => {
  if (!isSoundEnabled()) {
    console.log('Sound effects disabled in settings');
    return;
  }
  
  console.log('Playing correct sound...');
  
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = 523.25; // C5
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
    
    // Add second note for harmony
    const oscillator2 = ctx.createOscillator();
    const gainNode2 = ctx.createGain();
    
    oscillator2.connect(gainNode2);
    gainNode2.connect(ctx.destination);
    
    oscillator2.frequency.value = 659.25; // E5
    oscillator2.type = 'sine';
    
    gainNode2.gain.setValueAtTime(0.2, ctx.currentTime + 0.1);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    
    oscillator2.start(ctx.currentTime + 0.1);
    oscillator2.stop(ctx.currentTime + 0.4);
  } catch (error) {
    console.warn('Could not play correct sound:', error);
  }
};

// Play wrong answer sound (gentle downward tone)
export const playWrongSound = () => {
  if (!isSoundEnabled()) {
    console.log('Sound effects disabled in settings');
    return;
  }
  
  console.log('Playing wrong sound...');
  
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = 300;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.2);
  } catch (error) {
    console.warn('Could not play wrong sound:', error);
  }
};

// Play quiz completion sound (celebratory jingle)
export const playCompletionSound = () => {
  if (!isSoundEnabled()) {
    console.log('Sound effects disabled in settings');
    return;
  }
  
  console.log('Playing completion sound...');
  
  try {
    const ctx = getAudioContext();
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    const durations = [0.15, 0.15, 0.15, 0.4];
    let startTime = ctx.currentTime;
    
    notes.forEach((frequency, index) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + durations[index]);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + durations[index]);
      
      startTime += durations[index];
    });
  } catch (error) {
    console.warn('Could not play completion sound:', error);
  }
};

